import * as React from 'react'
import { useMemo } from 'react'

import Box from '@mui/material/Box'
import useTheme from '@mui/material/styles/useTheme'
import Stack from '@mui/material/Stack'
import Paper from '@mui/material/Paper'
import Checkbox from '@mui/material/Checkbox'
import Typography from '@mui/material/Typography'
import Switch from '@mui/material/Switch'

import type { ICustomFieldFormSetting } from '@user/store/entityFormSettings/typings'
import { ENTITY_TYPES } from '@sales-center/common'
import { useCustomSettingsCardHandlers } from '@user/components/Settings/EntityFormSettings/CustomSettings/hooks/useCustomSettingsCardHandlers'

import { getCardStyles } from './styling'

interface Props {
  settingItem: ICustomFieldFormSetting
  customSettings: ICustomFieldFormSetting[]
  entityType: ENTITY_TYPES
  position: 'first' | 'last' | 'common'
  isTidalThemeActive: boolean
}

/**
 * A single custom field's settings card: its name and active/required toggles.
 *
 * Every theme-dependent style comes from `getCardStyles`. The one remaining
 * branch on the theme is structural: the Tidal layout nests the checkbox and
 * label inside an extra row, whereas the legacy layout leaves them as siblings
 * and pushes the label right with its own `mr: auto`. Collapsing those into one
 * tree would change the rendered element count for one of the two themes.
 */
export function CustomSettingsCard(props: Props) {
  const { settingItem, position, isTidalThemeActive } = props
  const { required, enabled } = settingItem
  const { onChangeRequired, onChangeEnabled } =
    useCustomSettingsCardHandlers(props)
  const theme = useTheme()
  const styles = useMemo(
    () => getCardStyles(theme, isTidalThemeActive),
    [theme, isTidalThemeActive],
  )

  const checkbox = (
    <Checkbox
      onClick={(evt) => evt.stopPropagation()}
      onChange={onChangeEnabled}
      checked={enabled}
      color="primary"
      edge="start"
      size="medium"
    />
  )

  const requiredRow = (
    <Stack direction="row">
      <Switch
        size="small"
        checked={required}
        onChange={onChangeRequired}
        onClick={(evt) => evt.stopPropagation()}
      />
      <Typography variant="body2" sx={styles.requiredText}>
        Make field required
      </Typography>
    </Stack>
  )

  return (
    <Paper variant="outlined" sx={styles.getPaperSx(position)}>
      {isTidalThemeActive ? (
        <Stack
          direction="row"
          alignContent="center"
          justifyContent="space-between"
          p={2}
        >
          <Stack direction="row">
            <Stack
              justifyContent="center"
              alignItems="center"
              sx={styles.checkboxWrapper}
            >
              {checkbox}
            </Stack>
            <Stack>
              <Typography variant={styles.props.labelVariant} sx={styles.label}>
                {settingItem.name}
              </Typography>
              {requiredRow}
            </Stack>
          </Stack>
        </Stack>
      ) : (
        <Stack direction="row" p={2}>
          <Stack
            justifyContent="center"
            alignItems="center"
            sx={styles.checkboxWrapper}
          >
            {checkbox}
          </Stack>
          <Box ml={2} mr="auto">
            <Typography variant={styles.props.labelVariant} sx={styles.label}>
              {settingItem.name}
            </Typography>
            {requiredRow}
          </Box>
        </Stack>
      )}
    </Paper>
  )
}
